// @flow
import * as React from 'react';
import classNames from 'classnames';
import { Container, Col, Row } from 'reactstrap';
import { VCALENDAR, VEVENT } from 'ics-js';
import { FormattedMessage } from 'react-intl';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';

// Components
import OptInOutLoader from './OptInOutLoader';
import LoaderHOC from 'components/atoms/LoaderHOC';
import Feature from 'components/atoms/Feature/';
import DealerInfo from 'components/atoms/DealerInfo';
import PickUpLocationInfo from 'components/atoms/PickUpLocationInfo';
import Button from 'components/atoms/Button';
import { Icon } from 'components/atoms/Icon/';
import FlexPayPromo from 'components/atoms/FlexPayPromo';

// Utils
import { REVIEW_ROUTE } from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { convertToUTC } from 'utils/date';
import { isIEorEdge, getSchemaDomain, isStaticHosted } from 'utils/DOM';
import { isTrue } from 'utils/varValidation';

// Constants
import { APP_FLOW } from 'constants/appFlow';
import { MARKETING_STATUS } from 'constants/suscriptionStatus';
import { TRANSPORTATION } from 'constants/transportation';

import './WhiteConfirmation.scss';

const { VALET, VALETLOANER } = TRANSPORTATION;
const ns = 'white-confirmation';
const { BACKWARD } = NAVIGATION;
const { IM_NEW_HERE, FIND_ME, QUICK_APPT } = APP_FLOW;
const { NA, OPTED_IN, OPTED_OUT } = MARKETING_STATUS;

type Provider = {
  provider: string,
  launchUrl: string
};

type Props = {
  dealerSettings: Object,
  goTo: (path: string, direction: string) => void,
  gtmUserEvent: (
    element: string,
    action: string,
    result: string,
    location: string,
    value?: string
  ) => void,
  appointmentData: Object,
  dealerName: string,
  dealerServerUrl: string,
  deferredServicesIds: Array<number>,
  dealerContact: Object,
  apptNotModified: () => void,
  getTransportationOptions: (noNavigation?: boolean) => Object,
  appFlow: string,
  setAppFlow: (appFlow: string) => void,
  updatePackageInfo: () => void,
  dealerLogoURL: string,
  webKey: string,
  confirmationKey: string,
  symptomSurveyEnabled?: boolean,
  marketingSMSConfiguration?: boolean,
  optStatus: string,
  getOptOutCustomerForSMSMarketing: string => void,
  getOptInUrl: () => void,
  changeOptStatus: string => void,
  optInUrl: string,
  localeCode: string,
  intl: IntlShape,
  flexPayConfiguration?: boolean,
  flexPayProviders: Array<Provider>,
  externalValet: string,
  transportationOption: string
};

type State = {
  animationFlag: boolean
};

class WhiteConfirmation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    (this: any).modifyAppointment = this.modifyAppointment.bind(this);
    (this: any).goToNNA = this.goToNNA.bind(this);
  }

  state = {
    animationFlag: false
  };

  componentDidMount() {
    const {
      gtmUserEvent,
      appointmentData: { confirmationKey },
      dealerServerUrl
    } = this.props;

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      animationFlag: true
    });
    gtmUserEvent(
      'GTM_CURRENT_PAGE',
      'Displayed',
      'Display appointment confirmation number',
      'GTM_CURRENT_PAGE',
      confirmationKey
    );
    gtmUserEvent(
      'GTM_CURRENT_PAGE',
      'Usage',
      'Usage of schema name of dealer',
      'GTM_CURRENT_PAGE',
      dealerServerUrl
    );
  }

  getFlexPayProviders = () => {
    const { flexPayProviders, intl } = this.props;
    const uuidv1 = require('uuid/v1');
    return flexPayProviders.map(
      provider =>
        provider.provider &&
        provider.provider === 'DIGNIFI' && (
          <FlexPayPromo
            key={uuidv1()}
            flexPayUrl={provider.launchUrl}
            name={provider.provider}
            aria-label={intl.formatMessage({
              id: 'c8.aria.confirmation.go.to.flex.pay',
              defaultMessage: 'Go To Flex Pay'
            })}
          />
        )
    );
  };

  goToNNA() {
    const {
      dealerContact,
      dealerName,
      dealerLogoURL,
      webKey,
      confirmationKey
    } = this.props;
    const { streetAddressOne, city, state, postalCodeString } = dealerContact;
    const consumerProperties = {
      streetAddressOne,
      city,
      state,
      postalCodeString,
      dealerLogoURL,
      dealerName
    };
    const domain = isStaticHosted() ? getSchemaDomain() : '';
    window.open(
      `${domain}/symptomsurvey/?&webkey=${webKey}&confirmationKey=${confirmationKey}#/`,
      JSON.stringify(consumerProperties)
    );
  }

  getAppointmentOptions() {
    const { intl } = this.props;
    return (
      <React.Fragment>
        <Feature
          name="$saveToCalendar"
          render={() => (
            <Button
              className="btn-calendar indexed"
              buttonTheme="action"
              onClick={() => this.addToCalendar()}
              tabIndex={0}
            >
              <Icon
                type="calendar"
                className={`${ns}__icon`}
                aria-label={intl.formatMessage({
                  id: 'c8.confirmation.save.to.calendar'
                })}
              />
              <span aria-hidden={true}>
                {intl.formatMessage({ id: 'c8.confirmation.save.to.calendar' })}
              </span>
            </Button>
          )}
        />
        <Feature
          name="$editAppointment"
          render={() => (
            <Button
              className={`${ns}__edit-appt-btn indexed`}
              buttonTheme="link"
              onClick={this.modifyAppointment}
              tabIndex={0}
            >
              {intl.formatMessage({
                id: 'c8.confirmation.modify.appointment'
              })}
            </Button>
          )}
        />
      </React.Fragment>
    );
  }

  onMarketingOptInOptOutAction(optStatus: string) {
    const {
      changeOptStatus,
      getOptOutCustomerForSMSMarketing,
      localeCode,
      optInUrl
    } = this.props;
    if (optStatus === OPTED_IN) {
      getOptOutCustomerForSMSMarketing &&
        getOptOutCustomerForSMSMarketing(localeCode);
      changeOptStatus && changeOptStatus(OPTED_OUT);
    } else if (optStatus === NA && optInUrl) {
      window.open(optInUrl);
    }
  }

  getOptMessage(messageId: string) {
    return (
      messageId && (
        <FormattedMessage id={messageId}>{text => text}</FormattedMessage>
      )
    );
  }

  showOptInOut() {
    const { optStatus, intl } = this.props;
    let labelOptInOut = '';
    let buttonOptInOut = '';
    let buttonAriaKey = '';
    let defaultAriaValue = '';

    if (optStatus === NA) {
      buttonOptInOut = 'c8.confirmation.sign.up.for.text.marketing.messages';
      buttonAriaKey = 'c8.aria.confirmation.sign.up.for.marketing.messages';
      defaultAriaValue = 'Sign up for Marketing Messages';
    } else if (optStatus === OPTED_IN) {
      labelOptInOut = 'c8.confirmation.signed.up.for.text.marketing.messages';
      buttonOptInOut = 'c8.confirmation.opt.out';
    } else if (optStatus === OPTED_OUT) {
      labelOptInOut = 'c8.confirmation.customer.opted.out.from.text.marketing';
    }

    return (
      <LoaderHOC
        serviceId={'optStatus'}
        placeHolderType={() => <OptInOutLoader />}
        component={() => (
          <div className={`mb-4 pl-md-0`}>
            {optStatus !== NA && (
              <div>
                <Icon type="check_radio" className={`${ns}__icon-opt mr-1`} />
                <span className={`${ns}__opt-out-label`}>
                  {this.getOptMessage(labelOptInOut)}
                </span>
              </div>
            )}
            {optStatus !== OPTED_OUT && (
              <div>
                <Button
                  buttonTheme="link"
                  onClick={() => {
                    this.onMarketingOptInOptOutAction(optStatus);
                  }}
                  className={'indexed'}
                  tabIndex={0}
                  aria-label={
                    buttonAriaKey
                      ? intl.formatMessage({
                          id: buttonAriaKey,
                          defaultMessage: defaultAriaValue
                        })
                      : ''
                  }
                >
                  {this.getOptMessage(buttonOptInOut)}
                </Button>
              </div>
            )}
          </div>
        )}
      />
    );
  }

  getSupplementalOptions() {
    const {
      symptomSurveyEnabled,
      intl,
      marketingSMSConfiguration,
      flexPayConfiguration
    } = this.props;
    return (
      <React.Fragment>
        {isTrue(flexPayConfiguration) && (
          <div className={`${ns}__flex-pay mb-4`}>
            {this.getFlexPayProviders()}
          </div>
        )}
        {isTrue(marketingSMSConfiguration) && this.showOptInOut()}
        {isTrue(symptomSurveyEnabled) && (
          <React.Fragment>
            <span className={`${ns}__having-issues-label`}>
              {intl.formatMessage({
                id: 'c8.additional.issues.with.your.vehicle'
              })}
            </span>
            <Button
              buttonTheme="link"
              onClick={this.goToNNA}
              className={'indexed mb-4'}
              tabIndex={0}
              aria-label={intl.formatMessage({
                id: 'c8.aria.confirmation.continue.to.symptom.survey',
                defaultMessage: 'Continue to symptom survey'
              })}
            >
              {intl.formatMessage({
                id: 'c8.confirmation.describe.the.problem'
              })}
            </Button>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  modifyAppointment = async () => {
    const {
      goTo,
      apptNotModified,
      getTransportationOptions,
      appFlow,
      setAppFlow,
      updatePackageInfo
    } = this.props;
    if (appFlow === IM_NEW_HERE || appFlow === QUICK_APPT) {
      setAppFlow(FIND_ME);
    }
    try {
      await updatePackageInfo();
      await getTransportationOptions(true);
      apptNotModified();
      goTo(REVIEW_ROUTE, BACKWARD);
    } catch (err) {
      /* eslint-disable no-console */
      console.error(err);
      /* eslint-enable no-console */
    }
  };

  getEventDescription() {
    const {
      appointmentData,
      dealerName,
      deferredServicesIds,
      intl
    } = this.props;
    const {
      confirmationKey,
      advisorName,
      services,
      declinedServices,
      servicePoint,
      autoRecallServices,
      vehicle: { make, model, year },
      contactInfo: { firstName, lastName, phone }
    } = appointmentData;
    const packageName = servicePoint ? servicePoint.menuType.name : null;
    const declinedServicesNames = declinedServices.map(service => service.name);
    const deferredServicesNames = services.filter(
      service => deferredServicesIds.indexOf(service.id) > -1
    );
    const maintenanceServices = services.filter(
      service => service.serviceKind === 'maintenance'
    );
    const repairServices = services.filter(
      service => service.serviceKind === 'repair'
    );
    let description =
      intl.formatMessage({
        id: 'c8.save.to.calendar.detailed.information'
      }) +
      `\\n\\n` +
      intl.formatMessage(
        {
          id: 'c8.save.to.calendar.dealership.info'
        },
        { dealerName }
      ) +
      '\\n\\n' +
      intl.formatMessage({
        id: 'c8.save.to.calendar.appointment.info'
      }) +
      `\\n` +
      intl.formatMessage(
        {
          id: 'c8.save.to.calendar.advisor'
        },
        { advisorName }
      ) +
      `\\n` +
      intl.formatMessage(
        {
          id: 'c8.save.to.calendar.confirmation.code'
        },
        { confirmationKey }
      ) +
      `\\n\\n` +
      intl.formatMessage({
        id: 'c8.save.to.calendar.customer.info'
      }) +
      `\\n` +
      intl.formatMessage(
        {
          id: 'c8.save.to.calendar.name'
        },
        { firstName, lastName }
      ) +
      `\\n` +
      intl.formatMessage(
        {
          id: 'c8.save.to.calendar.phone'
        },
        { phone }
      ) +
      `\\n\\n` +
      intl.formatMessage(
        {
          id: 'c8.save.to.calendar.vehicle.info'
        },
        { make, model, year }
      ) +
      `\\n\\n` +
      intl.formatMessage({
        id: 'c8.save.to.calendar.service.info'
      }) +
      '\\n';
    description += packageName
      ? intl.formatMessage(
          {
            id: 'c8.save.to.calendar.packages'
          },
          { packageName }
        ) + '\\n'
      : '';
    if (maintenanceServices.length > 0) {
      description +=
        intl.formatMessage({
          id: 'c8.save.to.calendar.maintenance'
        }) + '\\n';
      maintenanceServices.forEach(service => {
        description += ` - ${service.name} \\n`;
      });
    }
    if (repairServices.length > 0) {
      description +=
        intl.formatMessage({
          id: 'c8.save.to.calendar.repair'
        }) + '\\n';
      repairServices.forEach(service => {
        description += ` - ${service.name} \\n`;
      });
    }
    if (autoRecallServices.length > 0) {
      description +=
        intl.formatMessage({
          id: 'c8.save.to.calendar.recall'
        }) + '\\n';
      autoRecallServices.forEach(service => {
        description += ` - ${service.name} \\n`;
      });
    }
    if (deferredServicesNames.length + declinedServicesNames.length > 0) {
      description +=
        intl.formatMessage({
          id: 'c8.save.to.calendar.declined'
        }) + '\\n';
      deferredServicesNames.concat(declinedServicesNames).forEach(service => {
        description += ` - ${service.name} \\n`;
      });
    }
    description +=
      '\\n\\n' +
      intl.formatMessage({
        id: 'c8.save.to.calendar.thanks.for.using.xtime.service.crm'
      }) +
      '\\n';
    return description;
  }

  buildCalendarEvent() {
    const {
      appointmentData: { startTime, endTime },
      dealerName,
      dealerContact,
      intl
    } = this.props;
    const {
      streetAddressOne,
      city,
      state,
      country,
      postalCodeString
    } = dealerContact;
    const cal = new VCALENDAR();
    const event = new VEVENT();
    const mailSummary = intl.formatMessage(
      {
        id: 'c8.save.to.calendar.service.appointment.at'
      },
      { dealerName }
    );
    cal.addProp('VERSION', 2);
    cal.addProp('METHOD', 'PUBLISH');
    cal.addProp('PRODID', dealerName);
    event.addProp('UID');
    event.addProp('DTSTAMP', convertToUTC(new Date(Date.now())));
    event.addProp('DTSTART', convertToUTC(startTime));
    event.addProp('DTEND', convertToUTC(endTime));
    event.addProp(
      'LOCATION',
      `${streetAddressOne}, ${city}, ${state}, ${postalCodeString}, ${country}`
    );
    event.addProp('SUMMARY', mailSummary);
    event.addProp('DESCRIPTION', this.getEventDescription());
    event.addProp('STATUS', 'CONFIRMED');
    event.addProp('PRIORITY', 3);
    cal.addComponent(event);

    return cal;
  }

  addToCalendar() {
    const {
      gtmUserEvent,
      dealerServerUrl,
      appointmentData: {
        reservationId,
        vehicle: { personId }
      }
    } = this.props;
    const cal = this.buildCalendarEvent();

    if (!isIEorEdge) {
      window.open(
        'data:text/calendar;charset=utf8,' + encodeURI(cal.toString())
      );
    } else {
      const ICSUrl = `${dealerServerUrl}/panama/exportVCalendar.do?RESERVATION_ID=${reservationId}&PID=${personId}&extension=.ics`;
      window.location.href = ICSUrl;
    }

    gtmUserEvent(
      'Add to Calendar Button',
      'Clicked',
      'Appointment Added to Calendar',
      'GTM_CURRENT_PAGE > Add to Calendar Button'
    );
  }

  render() {
    const { transportationOption, externalValet } = this.props;
    const className = classNames({
      'white-confirmation': true,
      'appearing-element': true,
      'animation-active': this.state.animationFlag
    });
    const isValet =
      transportationOption === VALET || transportationOption === VALETLOANER;
    return (
      <Container className={className}>
        <Row>
          <Col md={12} lg={4} className={`${ns}__info_container`}>
            {externalValet && isValet && <PickUpLocationInfo />}
            <DealerInfo />
          </Col>
          <Col md={12} lg={4} className={`${ns}__edit-appt-container`}>
            {this.getAppointmentOptions()}
          </Col>
          <Col md={12} lg={4} className={`${ns}__options-container`}>
            {this.getSupplementalOptions()}
          </Col>
        </Row>
      </Container>
    );
  }
}

export default WhiteConfirmation;



// WEBPACK FOOTER //
// ./src/components/pages/ConfirmationPage/components/WhiteConfirmation/WhiteConfirmation.js